import { IServerSideGetRowsRequest } from '@ag-grid-community/core';
import { createSelector } from '@ngrx/store';
import { AppState } from '../../app.state';
import { ITankProfile } from '@interfaces/tankProfile';
import { FilterExtensionsState } from '@state/models/filter-extensions.model';

const _selectGridFilter = (state: AppState) => state.gridFilter;
const _selectFilterModel = (state: AppState) => state.gridFilter?.filterModel;

const _selectFuzzySearch = (state: AppState) => state.gridFilterExtensions;

const _selectCurrentOrgId = (state: AppState) =>
  state.userInformation.lastSelectedOrganizationId;

const _selectLastSelectedTankProfile = (state: AppState) =>
  state.currentTankProfile;

export const selectLastSelectedTankProfile = createSelector(
  _selectLastSelectedTankProfile,
  (tp: ITankProfile) => tp,
);
const _selectLastSelectedTankProfileId = (state: AppState) =>
  state.currentTankProfile.tankProfileId;
export const selectLastSelectedTankProfileId = createSelector(
  _selectLastSelectedTankProfileId,
  (tpId: number) => tpId,
);

const _includeAffiliatedOrganization = (state: AppState) =>
  state.currentTankProfile.includeAffiliatedOrganization;
const _includeInactiveTanks = (state: AppState) =>
  state.currentTankProfile.includeInactiveTanks;
const _includeChildOrganization = (state: AppState) =>
  state.currentTankProfile.includeChildOrganization;
const _maintenanceTanksOnly = (state: AppState) =>
  state.currentTankProfile.maintenanceTanksOnly;
const _pageLength = (state: AppState) => state.currentTankProfile.pageLength;

export const selectGridQuery = createSelector(
  _selectCurrentOrgId,
  _includeAffiliatedOrganization,
  _includeInactiveTanks,
  _includeChildOrganization,
  _maintenanceTanksOnly,
  _pageLength,
  (
    currentOrgId: number,
    includeAffiliatedOrganization: boolean,
    includeInactiveTanks: boolean,
    includeChildOrganization: boolean,
    maintenanceTanksOnly: boolean,
    pageLength: number,
  ) => {
    return {
      organizationId: currentOrgId,
      tankProfile: {
        tankGridColumns: null,
        includeAffiliatedOrganization: includeAffiliatedOrganization,
        includeInactiveTanks: includeInactiveTanks,
        includeChildOrganization: includeChildOrganization,
        maintenanceTanksOnly: maintenanceTanksOnly,
        pageLength: pageLength,
      },
    };
  },
);

export const selectGridColumns = createSelector(
  _selectLastSelectedTankProfile,
  (tp) => tp.tankGridColumns,
);

export const selectWidgetQuery = createSelector(
  _selectCurrentOrgId,
  _includeAffiliatedOrganization,
  _includeInactiveTanks,
  _includeChildOrganization,
  _maintenanceTanksOnly,
  _selectGridFilter,
  (
    currentOrgId: number,
    includeAffiliatedOrganization: boolean,
    includeInactiveTanks: boolean,
    includeChildOrganization: boolean,
    maintenanceTanksOnly: boolean,
    gridRequestModel: IServerSideGetRowsRequest,
  ) => {
    if (!gridRequestModel) return null;

    return {
      ...gridRequestModel,
      TankProfile: {
        organizationId: currentOrgId,
        tankGridColumns: null,
        includeAffiliatedOrganization: includeAffiliatedOrganization,
        includeInactiveTanks: includeInactiveTanks,
        includeChildOrganization: includeChildOrganization,
        maintenanceTanksOnly: maintenanceTanksOnly,
      },
    };
  },
);

export const selectTankGridChange = selectWidgetQuery;
export const selectTankGridChangeForMap = createSelector(
  _selectCurrentOrgId,
  _includeAffiliatedOrganization,
  _includeInactiveTanks,
  _includeChildOrganization,
  _maintenanceTanksOnly,
  _selectGridFilter,
  _selectFuzzySearch,
  (
    currentOrgId: number,
    includeAffiliatedOrganization: boolean,
    includeInactiveTanks: boolean,
    includeChildOrganization: boolean,
    maintenanceTanksOnly: boolean,
    gridRequestModel: IServerSideGetRowsRequest,
    filterExtension: FilterExtensionsState,
  ) => {
    if (!gridRequestModel) return null;

    return {
      ...gridRequestModel,
      fuzzySearch: filterExtension?.fuzzySearch,
      TankProfile: {
        organizationId: currentOrgId,
        tankGridColumns: null,
        includeAffiliatedOrganization: includeAffiliatedOrganization,
        includeInactiveTanks: includeInactiveTanks,
        includeChildOrganization: includeChildOrganization,
        maintenanceTanksOnly: maintenanceTanksOnly,
      },
    };
  },
);;


export const selectGridFilter = createSelector(
  _selectGridFilter,
  (gridFilter: IServerSideGetRowsRequest) => gridFilter,
);

export const selectFilterModel = createSelector(
  _selectFilterModel,
  (filterModel: any) => filterModel,
);

//selector that drives when to refresh Tank Filter components
export const selectTankContextChanges = createSelector(
  _selectCurrentOrgId,
  _selectLastSelectedTankProfile,
  (currentOrgId: number, currentTankProfile: ITankProfile) => {
    return {
      currentOrgId: currentOrgId,
      tankProfile: currentTankProfile,
    };
  },
);
const _tankProfileId = (state: AppState) =>
  state.currentTankProfile.tankProfileId;

export const selectQuickFilterContextChange = createSelector(
  _selectCurrentOrgId,
  _tankProfileId,
  _includeAffiliatedOrganization,
  _includeInactiveTanks,
  _includeChildOrganization,
  _maintenanceTanksOnly,
  (currentOrgId: number, tankProfileId: number, includeAffiliatedOrganization: boolean, includeInactiveTanks: boolean, includeChildOrganization: boolean, maintenanceTanksOnly: boolean) => {
    return {
      currentOrgId: currentOrgId,
      tankProfileId: tankProfileId,
      includeAffiliatedOrganization: includeAffiliatedOrganization,
      includeInactiveTanks: includeInactiveTanks,
      includeChildOrganization: includeChildOrganization,
      maintenanceTanksOnly: maintenanceTanksOnly,
    };
  },
);

const _selectTankProfileManagement = (state: AppState) =>
  state.tankProfileManagement;

export const selectTankProfileDeleteResponse = createSelector(
  _selectTankProfileManagement,
  (result) => {
    return result?.deleteResult;
  },
);

